import React from "react";

import not from "ramda/src/not";
import map from "ramda/src/map";
import set from "ramda/src/set";
import lensProp from "ramda/src/lensProp";
import identity from "ramda/src/identity";
import prop from "ramda/src/prop";
import ifElse from "ramda/src/ifElse";
import compose from "ramda/src/compose";

import Api from "../../services/Api";
import useAuth from "../../routes/login/AuthContext";
import {
  useNetworkContext,
  useSelectedNetworkContext,
} from "../../contexts/NetworkContext/context";
import { ReadOnlyWrapper } from "../ReadOnlyWrapper";
import { useSnackbar } from "../../hooks/useSnackbar";
import { IExtendedL3 } from "../../services/NetworkApi";

import { Switch } from "../Switch";

interface IProps {
  l2Id: number;
  l3Id: number;
  defaultValue: boolean;
  isDisabled?: boolean;
  updateData: React.Dispatch<React.SetStateAction<IExtendedL3[]>>;
}

const getLabel = (isAllowed: boolean) =>
  isAllowed ? "Allowed" : "Not Allowed";

export const StatusSwitch: React.FC<IProps> = (props) => {
  const { getAuthToken, isSuperAdminRead } = useAuth();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = React.useState(false);
  const [value, setValue] = React.useState(props.defaultValue);
  const { updateUnitTypeValues } = useNetworkContext();
  const { selectedNetworkId } = useSelectedNetworkContext();

  React.useEffect(() => {
    setValue(props.defaultValue);
  }, [props.defaultValue]);

  const updateLocalNetworkData = React.useCallback(
    (newNewMatchingAllowed: boolean) => () => {
      updateUnitTypeValues(selectedNetworkId, props.l2Id, props.l3Id, {
        newNewMatchingAllowed,
        newNewFindingAllowed: newNewMatchingAllowed,
      });
    },
    [selectedNetworkId, props.l2Id, updateUnitTypeValues]
  );

  const updateData = (newNewMatchingAllowed: boolean) => () => {
    props.updateData(
      map(
        compose(
          ifElse(
            (l3: any) => l3.l3Id === props.l3Id && l3.l2Id === props.l2Id,
            set(lensProp("newNewMatchingAllowed"), newNewMatchingAllowed),
            identity
          ),
          ifElse(
            (l3) => l3.l3Id === props.l3Id && l3.l2Id === props.l2Id,
            set(
              lensProp("newNewFindingAllowed"),
              newNewMatchingAllowed
            ),
            identity
          )
        )
      )
    );
  };

  const stopLoading = () => setIsLoading(false);

  const handleError = () =>
    showErrorSnackbar("An error occurred. Please try again");

  const onComplete = () => showSuccessSnackbar("Saved Changes");

  const handleSuccess = (newNewMatchingAllowed: boolean) =>
    ifElse(
      prop("success"),
      compose(
        onComplete,
        updateLocalNetworkData(newNewMatchingAllowed),
        updateData(newNewMatchingAllowed)
      ),
      handleError
    );

  const handleChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    allowNewNewMatching: boolean
  ) => {
    const body = {
      subnetworkCommands: [],
      unitTypeCommands: [
        {
          l1Id: selectedNetworkId,
          l2Id: props.l2Id,
          l3Id: props.l3Id,
          allowNewNewMatching,
          allowNewNewFinding: allowNewNewMatching,
        },
      ],
    };

    setIsLoading(true);
    setValue(not);

    Api.setMathingRules(selectedNetworkId, body, await getAuthToken())
      .then(handleSuccess(allowNewNewMatching))
      .catch(handleError)
      .finally(stopLoading);
  };

  return (
    <ReadOnlyWrapper>
      <Switch
        isDisabled={isLoading || isSuperAdminRead() || props.isDisabled}
        label={getLabel(value)}
        switchProps={{ checked: value, onChange: handleChange }}
      />
    </ReadOnlyWrapper>
  );
};
